import { Box, Button, useTheme } from '@mui/material';
import CodeEditor from '../../CodeEditor';
import { useEffect, useRef, useState } from 'react';
import { API_PATCH, DBRequest, useConfig } from '../../../../api/api';
import { tokens } from '../../../../global/theme/tokens';
import Loader from '../../../special/Loader';
import TemplateVariables from '../TemplateVariables';
import ExoDialog from '../../../exo/ExoDialog';
import { useTranslation } from 'react-i18next';
import TypeText from '../../../form/form-fields/TypeText';
import findDifferences from '../../../exo/findDifferencesInDataCollection';
import StyledTextContainer from '../../../preview/StyledTextContainer';
import RenderHTML from '../../../preview/RenderHTML';
import TemplateVarSwitcher from '../../../templates/TemplateVarSwitcher';

const TemplateBlockEditor = ({
  data,
  onSave = () => {},
  onClose = () => {},
  open = true,
  onFeedback = () => {},
  variables = [],
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const aceEditorRef = useRef(null);

  const [block, setBlock] = useState(null);

  useEffect(() => {
    if (data && data.id)
      DBRequest({
        config,
        path: `template-blocks/${data.id}`,
        onResponse: (res) => setBlock(res.data),
      });
  }, [data, config]);

  // ACTIONS --------
  function handleCodeChange(d) {
    setBlock({
      ...block,
      value: d,
    });
  }

  const handleClickVariable = (textToInsert) => {
    const currentPos = aceEditorRef.current.editor.getCursorPosition();
    const text = aceEditorRef.current.editor.getValue();
    const beforeText = text.substring(
      0,
      aceEditorRef.current.editor.session.doc.positionToIndex(currentPos)
    );
    const afterText = text.substring(
      aceEditorRef.current.editor.session.doc.positionToIndex(currentPos),
      text.length
    );

    aceEditorRef.current.editor.setValue(beforeText + textToInsert + afterText);
    aceEditorRef.current.editor.clearSelection();
    aceEditorRef.current.editor.moveCursorToPosition(currentPos);
  };

  function handleSave() {
    const diff = findDifferences(data, block);
    DBRequest({
      config,
      path: 'template-blocks/' + data.id,
      method: API_PATCH({ ...diff, vars: null }),
      onResponse: onSave,
      onFeedback: onFeedback,
    });
    onClose();
  }

  const [disableTemplateRendering, setDisableTemplateRendering] =
    useState(false);

  return (
    <Box className="flex flex-col h-full w-full">
      {!block ? (
        <Loader active={true} message="Template Loading..." />
      ) : (
        <Box className="flex flex-col h-full w-full overflow-y-auto justify-between">
          <Box className="flex flex-row h-full w-full overflow-hidden gap-2">
            {block.vars && (
              <TemplateVariables
                className="min-w-[250px] max-w-[200px]"
                variables={block.vars}
                onClick={handleClickVariable}
              />
            )}
            <Box className="flex flex-col h-full w-full overflow-hidden pt-2">
              <Box className="flex gap-2 w-full">
                <TypeText
                  size="small"
                  field={{ label: t('Name'), key: 'label' }}
                  dataCollection={block}
                  updateValidationOnChange={(key, value) =>
                    setBlock({ ...block, [key]: value })
                  }
                />
              </Box>
              {block && (
                <CodeEditor
                  className="w-full"
                  editorRef={aceEditorRef}
                  data={block?.value}
                  currentKey={data?.id}
                  setData={handleCodeChange}
                  height={500}
                  variables={block?.vars || []}
                  disableTemplateRendering={disableTemplateRendering}
                  changeDisableTemplateRendering={setDisableTemplateRendering}
                />
              )}
            </Box>
            <StyledTextContainer autoOverflow noWidth>
              <RenderHTML
                sx={{ padding: '20px!important' }}
                HTML={
                  block.value
                    ? disableTemplateRendering
                      ? block.value
                      : TemplateVarSwitcher(block.value, block.vars)
                    : ''
                }
              />
            </StyledTextContainer>
          </Box>
          <Box className="flex justify-between p-2">
            <Button color="error" onClick={onClose} variant="outlined">
              {t('Cancel')}
            </Button>
            <Button color="success" variant="contained" onClick={handleSave}>
              {t('Save')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TemplateBlockEditor;
